import { SelfContainedCommand, CommandExecutionContext } from '../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../types/standardToolResults';
import { API_BASE_URL } from "../api/config";
import { createFileLogger } from '../utils/logger';
import { toolFetchCommand } from '../components/Space/SpaceChat/commands/tool_fetch';
import { traceService } from '../services/debug/traceService';
import { webSearchCommand } from './aicommands/web_search';
import { verifyCitationsCommand } from './aicommands/verify_citations';
import { retrievePdfMetadataCommand } from './aicommands/retrieve_pdf_metadata';
import { notificationService } from '../services/notificationService';
import { contextCommands, integrationCommands } from '../systemCommands/integrations';
import { MANIFEST as SEARCH_TOOLS_MANIFEST } from '../systemCommands/agentTools/search_tools';

const log = createFileLogger('AICOMMANDS');

console.log('[aiCommands] Module loaded - includes playbook_execute');

// Helper function to execute streaming AI commands with proper SSE handling
const executeStreamingAICommand = async (
  context: CommandExecutionContext,
  endpoint: string,
  prompt: string,
  config: any = {}
): Promise<any> => {
  const streamLog = createFileLogger('StreamingAI');
  const toolUseId = context.executionId;
  const startTime = Date.now();
  
  // Trace the start of the streaming AI command
  traceService.log({
    traceId: toolUseId,
    type: 'streaming_ai_start',
    data: {
      endpoint,
      promptLength: prompt.length,
      model: config.model || 'claude-opus-4-1-20250805',
      useWebSearch: config.useWebSearch || false,
      spaceId: context.spaceId
    },
    metadata: { startTime }
  });
  
  try {
    const authToken = localStorage.getItem('authToken');

    // Build context for Claude
    const spaceContext = context.spaceContext || {};
    const windowContents = (spaceContext as any).entityRefs || {};
    // Get explicit content (user-provided text snippets)
    const explicitContent = ((spaceContext as any).explicitContent || []).map((exc: any) => {
      if (!exc) return exc;
      const sc = exc.structuredContent;
      const hasNodes = Array.isArray(sc?.nodes) && sc.nodes.length > 0;
      const hasText = typeof sc?.text === 'string' && sc.text.length > 0;
      const hasMd = typeof sc?.markdown === 'string' && sc.markdown.length > 0;
      const hasMetaNodes = typeof sc?.metadata?.totalNodes === 'number' && sc.metadata.totalNodes > 0;
      const useStructured = hasNodes || hasText || hasMd || hasMetaNodes;
      return useStructured ? { ...exc, text: '' } : exc;
    });

    // Use the existing working Claude streaming endpoint instead of wizard
    const response = await fetch(`${API_BASE_URL}/claude/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      credentials: 'include',
      mode: 'cors',
      body: JSON.stringify({
        query: prompt,
          context: {
          threadId: 'temp-streaming-thread', // Temp thread for streaming
          spaceId: context.spaceId || 'default',
          windowContents: windowContents,
          explicitContent: explicitContent
        },
        conversation_history: [], // Fresh conversation for AI commands
        model: config.model || 'claude-opus-4-1-20250805',
        use_rag: false // Don't use RAG for AI commands
      })
    });



    if (!response.ok) {
      let errorMessage = `Streaming request failed: ${response.status}`;
      try {
        const errorText = await response.text();
        if (errorText) {
          errorMessage += ` - ${errorText}`;
        }
      } catch { /* ignore */ }
      throw new Error(errorMessage);
    }

    if (!response.body) {
      throw new Error('No response body available for streaming');
    }

    // Process the stream
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    
    let finalResult: any = null;
    let accumulatedContent = '';
    let accumulatedThinking = '';
    let eventCount = 0;
    
    streamLog.log('[StreamingAI] Starting stream processing');
    
    try {
      while (true) {
        // Check for cancellation
        if (context.signal?.aborted) {
          streamLog.log('[StreamingAI] Stream cancelled by user');
          throw new Error('Stream cancelled by user');
        }
        
        const { done, value } = await reader.read();
        
        if (done) {
          streamLog.log('[StreamingAI] Stream ended', {
            eventCount,
            contentLength: accumulatedContent.length,
            hasFinalResult: !!finalResult
          });
          break;
        }
        
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (!line.trim()) continue;
          
          try {
            let cleanLine = line;
            if (line.startsWith('data: ')) {
              cleanLine = line.substring(6);
            }
            
            const data = JSON.parse(cleanLine);
            eventCount++;
            
            // ✅ Call onProgress with streaming data for real-time ToolUseCard updates
            // First, check for Anthropic's new streaming format
            if (data.type === 'content_block_delta') {
                if (data.delta.type === 'text_delta') {
                    accumulatedContent += data.delta.text;
                } else if (data.delta.type === 'input_json_delta') {
                    // This is likely part of the "thinking" process
                    accumulatedThinking += data.delta.input_json_delta;
                }
            } 
            // Fallback for the simpler format
            else if (data.type === 'text' && data.content) {
              accumulatedContent += data.content;
            }

            // Call onProgress with the latest state
            if (context.onProgress) {
              context.onProgress({
                type: 'streaming_update',
                toolUseId: toolUseId,
                activity: accumulatedContent ? 'Generating content...' : 'Thinking...',
                thinking_total: accumulatedThinking,
                text_total: accumulatedContent,
              });
            }
            
            // Handle done event
            if (data.type === 'done' || data.type === 'message_stop') {
              finalResult = {
                content: accumulatedContent,
                thinking: accumulatedThinking,
                conversation_title: data.conversation_title || ''
              };
            }
            
          } catch (parseError: unknown) {
            streamLog.warn('[StreamingAI] Failed to parse stream line:', {
              error: parseError instanceof Error ? parseError.message : String(parseError),
              line: line.substring(0, 100)
            });
          }
        }
      }
      
      if (!finalResult) {
        finalResult = { content: accumulatedContent, thinking: accumulatedThinking };
      }
      
      streamLog.log('[StreamingAI] ✅ Streaming completed successfully', {
        contentLength: accumulatedContent.length,
        executionTime: Date.now() - startTime
      });

      // Trace successful completion
      traceService.log({
        traceId: toolUseId,
        type: 'streaming_ai_complete',
        data: {
          contentLength: accumulatedContent.length,
          thinkingLength: accumulatedThinking.length,
          eventCount,
          success: true
        },
        metadata: { 
          executionTime: Date.now() - startTime,
          endTime: Date.now()
        }
      });

      return {
        status: 'completed',
        output: {
          status: 'success',
          message: 'Streaming AI request completed',
          format: 'json',
          data: finalResult,
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'self-contained'
          }
        }
      };

    } catch (streamError) {
      streamLog.error('[StreamingAI] Stream processing error:', streamError);
      throw streamError;
    } finally {
      try {
        reader.releaseLock();
      } catch {
        // Reader might already be closed
      }
    }

  } catch (error) {
    streamLog.error('[StreamingAI] Request failed:', error);
    
    // Trace error
    traceService.log({
      traceId: toolUseId,
      type: 'streaming_ai_error',
      data: {
        error: error instanceof Error ? error.message : String(error),
        success: false
      },
      metadata: { 
        executionTime: Date.now() - startTime,
        errorTime: Date.now()
      }
    });
    
    throw error;
  }
};

export const aiCommands: SelfContainedCommand[] = [
  {
    id: 'task_plan_steps',
    description: 'Generate a simple todo list for the task',
    
    schema: {
      parameters: [
        {
          name: 'userGoal',
          type: 'string',
          description: 'The user\'s goal or task description',
          required: true
        },
        {
          name: 'todoList',
          type: 'array',
          description: 'A list of 3-6 specific, actionable todo steps (as strings) that break down the user goal into logical sequence. Each step should be clear, CONCISE (<10 words) and executable.',
          required: true
        }
      ]
    },

    examples: {
      good: [
        {
          input: { 
            userGoal: 'Format my research paper to NEJM guidelines',
            todoList: ['Read the current document', 'Research formatting requirements', 'Apply formatting changes', 'Review and finalize']
          },
          expectedOutput: createSuccessResult('Todo list generated', { 
            todos: ['Read the current document', 'Research formatting requirements', 'Apply formatting changes', 'Review and finalize'],
            totalSteps: 4,
            currentStep: 0
          }),
          description: 'Generate todos for document formatting task'
        }
      ],
      bad: [
        {
          input: { userGoal: '', todoList: [] },
          error: 'User goal cannot be empty',
          why: 'Empty user goal provided',
          fix: 'Provide valid user goal description'
        },
        {
          input: { userGoal: 'Valid goal', todoList: [] },
          error: 'Todo list cannot be empty',
          why: 'Empty todo list provided',
          fix: 'Provide valid todo list array'
        }
      ]
    },

    handler: async (params, context: CommandExecutionContext): Promise<StandardToolResult> => {
      const { userGoal, todoList } = params;
      
      // Trace command start
      traceService.log({
        traceId: context.executionId,
        type: 'ai_command_start',
        data: {
          commandId: 'task_plan_steps',
          userGoal: userGoal?.substring(0, 100),
          todoCount: todoList?.length || 0
        }
      });
      
      // Debug logging to see what the AI is generating
      console.log('[task_plan_steps] 🔍 Received parameters:', {
        userGoal,
        todoList,
        todoCount: todoList?.length || 0,
        timestamp: new Date().toISOString(),
        executionId: context.executionId
      });
      
      // Validate inputs
      if (!userGoal || userGoal.trim() === '') {
        return createErrorResult(
          'User goal cannot be empty',
          'Please provide a valid user goal description',
          'validation'
        );
      }

      if (!todoList || !Array.isArray(todoList) || todoList.length === 0) {
        return createErrorResult(
          'Todo list cannot be empty',
          'Please provide a valid todo list array',
          'validation'
        );
      }

      // Use the AI-generated todo list
      const todos: string[] = todoList.filter(todo => todo && todo.trim() !== '');

      if (todos.length === 0) {
        return createErrorResult(
          'All todo items are empty',
          'Please provide valid todo items',
          'validation'
        );
      }

      // Trace successful completion
      traceService.log({
        traceId: context.executionId,
        type: 'ai_command_complete',
        data: {
          commandId: 'task_plan_steps',
          success: true,
          todosGenerated: todos.length
        }
      });

      return createSuccessResult('Todo list generated', {
        todos,
        totalSteps: todos.length,
        currentStep: 0
      });
    }
  },
  {
    id: 'task_classify',
    description: 'Classify user intent for task routing and agentic workflow detection',
    autonomyLevel: 'auto',
    visualTreatment: 'silent', // This runs silently
    
    schema: {
      parameters: [
        {
          name: 'userMessage',
          type: 'string',
          description: 'The user\'s message to classify',
          required: true,
          validator: (value: string) => {
            if (!value || value.trim().length === 0) return 'User message cannot be empty';
            return null;
          }
        },
        {
          name: 'classificationPrompt', 
          type: 'string',
          description: 'Specific classification prompt for the domain',
          required: true,
          validator: (value: string) => {
            if (!value || value.trim().length === 0) return 'Classification prompt cannot be empty';
            return null;
          }
        },
        {
          name: 'contextType',
          type: 'string',
          description: 'Type of classification context (agentic_detection, task_routing, etc.)',
          required: true,
          validator: (value: string) => {
            if (!value || value.trim().length === 0) return 'Context type cannot be empty';
            return null;
          }
        }
      ]
    },

    examples: {
      good: [
        {
          input: { 
            userMessage: 'Using these PDFs, enhance my methods section',
            classificationPrompt: 'Determine if agentic...',
            contextType: 'agentic_detection'
          },
          expectedOutput: createSuccessResult('Task classified', { 
            classification: { isAgentic: true, confidence: 0.9 }
          }),
          description: 'Classify agentic document synthesis task'
        }
      ],
      bad: [
        {
          input: { userMessage: '', classificationPrompt: 'test', contextType: 'test' },
          error: 'User message cannot be empty',
          why: 'Empty user message provided',
          fix: 'Provide valid user message'
        }
      ]
    },

    performance: {
      expectedDuration: 200, // Fast with Claude Haiku
      complexity: 'low'
    },

    handler: async (params, context: CommandExecutionContext): Promise<StandardToolResult> => {
      const startTime = Date.now();
      const { userMessage, classificationPrompt, contextType } = params;
      
      // Trace command start
      traceService.log({
        traceId: context.executionId,
        type: 'ai_command_start',
        data: {
          commandId: 'task_classify',
          contextType,
          messageLength: userMessage.length,
          promptLength: classificationPrompt.length
        }
      });
      
      try {
        log.log('[task_classify] Starting classification:', {
          userMessage: userMessage.substring(0, 100) + '...',
          contextType,
          hasPrompt: !!classificationPrompt
        });

        // Use streaming AI command with Claude Haiku for fast, cheap classification
        const result = await executeStreamingAICommand(
          context,
          'claude/chat/stream',
          classificationPrompt,
          {
            model: 'claude-opus-4-1-20250805', // High quality for classification
            max_tokens: 300,
            useWebSearch: false,
            temperature: 0.1, // Low temperature for consistent classification
            enableThinking: false // No need for thinking on classification
          }
        );

        if (!result.output || result.output.status !== 'success') {
          return createErrorResult(
            'Classification failed',
            result.error || 'Streaming AI command failed',
            'execution',
            { metadata: { executionTime: Date.now() - startTime } }
          );
        }

        // Parse classification result
        let classification;
        try {
          // Look for JSON in the response
          const responseText = result.output.data.content || result.output.data.parsedContent || '';
          const jsonMatch = responseText.match(/\{[\s\S]*\}/);
          
          if (jsonMatch) {
            classification = JSON.parse(jsonMatch[0]);
          } else {
            // Fallback: create simple classification from text response
            classification = {
              isAgentic: responseText.toLowerCase().includes('true') || responseText.toLowerCase().includes('agentic'),
              confidence: 0.5,
              reasoning: 'Parsed from text response'
            };
          }
        } catch {
          log.warn('[task_classify] Failed to parse JSON, using fallback classification');
          classification = {
            isAgentic: false,
            confidence: 0.1,
            reasoning: 'Failed to parse classification response'
          };
        }

        log.log('[task_classify] Classification completed:', {
          contextType,
          classification,
          executionTime: Date.now() - startTime
        });

        // Trace successful completion
        traceService.log({
          traceId: context.executionId,
          type: 'ai_command_complete',
          data: {
            commandId: 'task_classify',
            success: true,
            contextType,
            classification
          },
          metadata: { executionTime: Date.now() - startTime }
        });

        return createSuccessResult('Task classified successfully', {
          classification,
          metadata: {
            executionTime: Date.now() - startTime,
            model: 'claude-opus-4-1-20250805',
            contextType,
            userMessageLength: userMessage.length
          }
        });

      } catch (error: any) {
        log.error('[task_classify] ❌ Classification failed:', error);
        
        // Trace error
        traceService.log({
          traceId: context.executionId,
          type: 'ai_command_error',
          data: {
            commandId: 'task_classify',
            success: false,
            error: error instanceof Error ? error.message : String(error)
          },
          metadata: { executionTime: Date.now() - startTime }
        });
        
        return createErrorResult(
          'Task classification failed',
          error,
          'execution',
          { metadata: { executionTime: Date.now() - startTime } }
        );
      }
    }
  },
  webSearchCommand,

  {
    id: 'task_complete',
    description: `Call when finished with the user's request

**EXAMPLE:**

{ "confidence": 100 }`,
    autonomyLevel: 'auto',
    visualTreatment: 'prominent',
    
    schema: {
      parameters: [
        {
          name: 'confidence',
          type: 'number',
          description: 'Confidence score that the task is complete (0-100)',
          required: true,
          defaultValue: 100,
          validator: (value: number) => {
            if (typeof value !== 'number') return 'confidence must be a number';
            if (value < 0 || value > 100) return 'confidence must be between 0 and 100';
            return null;
          }
        }
      ]
    },
    
    examples: {
      good: [
        { 
          input: { confidence: 100 },
          expectedOutput: createSuccessResult('Task Complete', { completed: true, confidence: 100 }, { format: 'json' }),
          description: 'Signal task completion with full confidence'
        },
        {
          input: { confidence: 85 },
          expectedOutput: createSuccessResult('Task Complete', { completed: true, confidence: 85 }, { format: 'json' }),
          description: 'Signal task completion with high confidence'
        }
      ],
      bad: []
    },
    
    handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
      const { confidence = 100 } = params;
      
      // Trace task completion
      traceService.log({
        traceId: context.executionId,
        type: 'task_complete',
        data: {
          commandId: 'task_complete',
          spaceId: context.spaceId,
          confidence
        }
      });
      
      log.log('[TASK_COMPLETE] Agent signaled task completion:', {
        executionId: context.executionId,
        spaceId: context.spaceId,
        confidence
      });
      
      return createSuccessResult(
        'Task Complete',
        {
          completed: true,
          confidence,
          timestamp: Date.now()
        },
        {
          format: 'json',
          metadata: {
            method: 'task_complete'
          }
        }
      );
    }
  } as SelfContainedCommand,
  
  // Artifact fetch command
  toolFetchCommand,
  
  // Citation verification command
  verifyCitationsCommand,
  
  // PDF metadata retrieval command (Zotero-style)
  retrievePdfMetadataCommand,
  
  // Playbook execution command
  {
    id: 'playbook_execute',
    description: `Execute a playbook workflow on provided content or entity.

WHEN TO USE: User explicitly asks to "run", "execute", or "trigger" a playbook. They will mention a playbook by name or with @ symbol.

HOW IT WORKS:
1. Finds the playbook by ID or name (case-insensitive search)
2. Creates a Job and PlaybookRun entity
3. Worker executes the playbook with AI and all available tools
4. Returns progress updates and completion notification

Examples:
- "Run @playbook on @file.pdf" → Use this tool with playbookId="playbook" and targetEntityId="file.pdf"
- "Execute the M&A playbook on this folder" → Use this tool with playbookId="M&A" and targetEntityId={folderId}
- "@Playbook analyze @document" → Use this tool with playbookId="Playbook" and targetEntityId="document"

IMPORTANT: When user mentions a playbook (with @ or by name) and asks to run/execute it, ALWAYS use this tool.`,
    
    visualTreatment: 'prominent',
    autonomyLevel: 'auto',
    
    schema: {
      parameters: [
        {
          name: 'playbookId',
          type: 'string',
          description: 'ID or LLM ID of the playbook entity to execute. When user mentions @Playbook, extract the llmId from the resolved mention format: @{document: Playbook, llmId: playbook_xxx}',
          required: true
        },
        {
          name: 'targetEntityId',
          type: 'string',
          description: 'Optional: Entity ID to process (folder, file, document). Extract llmId from @{...} mentions if present.',
          required: false
        },
        {
          name: 'content',
          type: 'string',
          description: 'Optional: Direct content to process (for quick runs)',
          required: false
        },
        {
          name: 'context',
          type: 'object',
          description: 'Optional: Additional context for the playbook',
          required: false
        }
      ]
    },
    
    examples: {
      good: [
        {
          input: { playbookId: 'playbook_ma_workflow', targetEntityId: 'folder_deals' },
          expectedOutput: createSuccessResult('Playbook M&A Workflow completed', {
            jobId: 'job_123',
            playbookRunId: 'run_456',
            status: 'succeeded',
            createdEntityIds: ['doc_1', 'doc_2'],
            toolCallCount: 15,
            duration: 120
          }),
          description: 'Execute M&A playbook on a deals folder'
        }
      ],
      bad: [
        {
          input: { playbookId: 'invalid' },
          error: 'Playbook not found',
          why: 'The specified playbook ID does not exist',
          fix: 'Use a valid playbook ID or LLM ID'
        }
      ]
    },
    
    handler: async (params, context) => {
      const { playbookId, targetEntityId, content: directContent, context: userContext } = params;
      
      console.log('[playbook_debug] ========================================');
      console.log('[playbook_debug] Playbook execute tool called');
      console.log('[playbook_debug] Playbook ID:', playbookId);
      console.log('[playbook_debug] Target Entity ID:', targetEntityId);
      console.log('[playbook_debug] Has direct content:', !!directContent);
      console.log('[playbook_debug] User context:', userContext);
      console.log('[playbook_debug] Space ID:', context.spaceId);
      console.log('[playbook_debug] User ID:', context.userId);
      console.log('[playbook_debug] Context entityContexts:', (context.spaceContext as any)?.entityContexts);
      console.log('[playbook_debug] Context entityRefs:', Object.keys((context.spaceContext as any)?.entityRefs || {}));
      console.log('[playbook_debug] ========================================');
      
      try {
        const authToken = localStorage.getItem('authToken');
        
        // 1. Use entity_get to fetch playbook (more reliable than custom endpoint)
        console.log('[playbook_debug] Fetching playbook entity using entity_get...');
        
        const entityGetCommand = context.services?.entityService?.get;
        let playbook;
        
        if (entityGetCommand) {
          // Use entity service if available
          try {
            playbook = await entityGetCommand(playbookId);
            console.log('[playbook_debug] Playbook fetched via entity service:', playbook?.name);
          } catch (e) {
            console.error('[playbook_debug] Entity service failed:', e);
          }
        }
        
        // Fallback: Use entity store directly
        if (!playbook) {
          console.log('[playbook_debug] Trying entityStore fallback...');
          const useEntityStore = (await import('../stores/entityStore')).default;
          const entityState = useEntityStore.getState();
          
          // Try by _id or llmId
          playbook = entityState.entities.find((e: any) => 
            (e._id === playbookId || e.llmId === playbookId) && 
            e.entityType === 'Playbook'
          );
          
          if (playbook) {
            console.log('[playbook_debug] Found playbook in entityStore:', playbook.name);
          }
        }
        
        if (!playbook || playbook.entityType !== 'Playbook') {
          console.error('[playbook_debug] Playbook not found:', playbookId);
          return createErrorResult(
            'Playbook not found',
            `Could not find playbook with ID: ${playbookId}. Make sure the playbook exists in the current space.`,
            'validation'
          );
        }
        
        console.log('[playbook_debug] Using playbook:', playbook.name, '(ID:', playbook._id, ')');
        
        // 2. Prepare trigger payload
        console.log('[playbook_debug] Preparing trigger payload...');
        const triggerPayload: any = {
          triggeredBy: 'chat_tool',
          userId: context.userId,
          spaceId: context.spaceId,
          triggeredAt: new Date().toISOString()
        };
        
        if (targetEntityId) {
          console.log('[playbook_debug] Using target entity:', targetEntityId);
          triggerPayload.entityId = targetEntityId;
        }
        
        if (directContent) {
          console.log('[playbook_debug] Using direct content (length:', directContent.length, ')');
          triggerPayload.content = directContent;
        }
        
        if (userContext) {
          console.log('[playbook_debug] Adding user context to trigger');
          triggerPayload.context = userContext;
        }
        
        console.log('[playbook_debug] Trigger payload prepared:', triggerPayload);
        
        // 3. Create PlaybookRun and Job
        console.log('[playbook_debug] Calling execute endpoint...');
        const response = await fetch(`${API_BASE_URL}/playbooks/execute`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
          },
          body: JSON.stringify({
            playbookId: playbook._id,
            triggerPayload,
            spaceId: context.spaceId,
            userId: context.userId
          })
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('[playbook_debug] Execute endpoint failed:', response.status, errorText);
          throw new Error(`Failed to start playbook: ${response.statusText}`);
        }
        
        const { jobId, playbookRunId } = await response.json();
        
        console.log('[playbook_debug] Execute response:', { jobId, playbookRunId });
        console.log('[playbook_debug] Job ID:', jobId);
        console.log('[playbook_debug] Playbook Run ID:', playbookRunId);
        
        // 4. Subscribe to job progress via SSE
        // EventSource doesn't support custom headers, so pass token as query param
        const progressAuthToken = localStorage.getItem('authToken');
        const progressUrl = `${API_BASE_URL}/playbooks/jobs/${jobId}/progress${progressAuthToken ? `?token=${encodeURIComponent(progressAuthToken)}` : ''}`;
        console.log('[playbook_debug] Opening SSE connection:', progressUrl);
        const eventSource = new EventSource(progressUrl);
        
        eventSource.onopen = () => {
          console.log('[playbook_debug] SSE connection opened successfully');
        };
        
        // Track tool events for display
        const toolEvents: any[] = [];
        let lastProgressUpdate: any = {
          phase: 'starting',
          percentage: 0,
          stats: {}
        };
        
        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            console.log('[playbook_debug] SSE event received:', data.type);
            
            if (data.type === 'progress' && context.onProgress) {
              const progress = data.progress;
              console.log('[playbook_debug] Progress update:', {
                phase: progress?.phase,
                percentage: progress?.percentage,
                currentAction: progress?.currentAction,
                stats: progress?.stats
              });
              
              lastProgressUpdate = {
                phase: progress?.phase || 'executing',
                percentage: progress?.percentage || 0,
                stats: progress?.stats || {}
              };
              
              context.onProgress({
                type: 'streaming_update',
                activity: progress?.currentAction || 'Processing...',
                message: progress?.currentAction || 'Processing...',
                structuredProgress: {
                  ...lastProgressUpdate,
                  toolEvents: [...toolEvents]
                }
              });
            } else if (data.type === 'tool_event' && context.onProgress) {
              const toolEvent = data.event;
              toolEvents.push(toolEvent);
              
              console.log('[playbook_debug] Tool event:', toolEvent.type, toolEvent.toolName, '(total events:', toolEvents.length, ')');
              
              context.onProgress({
                type: 'streaming_update',
                activity: `${toolEvent.toolName} - ${toolEvent.type}`,
                message: `${toolEvent.toolName} - ${toolEvent.type}`,
                structuredProgress: {
                  ...lastProgressUpdate,
                  toolEvents: [...toolEvents]
                }
              });
            }
          } catch (parseError) {
            console.warn('[playbook_debug] Failed to parse SSE onmessage event:', parseError);
          }
        };
        
        // 5. Wait for completion
        console.log('[playbook_debug] Waiting for completion...');
        return new Promise((resolve) => {
          let timeoutId: ReturnType<typeof setTimeout>;

          // Single finish path — clears timeout, closes socket, resolves promise
          const finish = (result: any) => {
            clearTimeout(timeoutId);
            eventSource.close();
            resolve(result);
          };

          eventSource.onerror = () => {
            console.error('[playbook_debug] SSE connection error — resolving with failure');
            finish(createErrorResult(
              'Connection lost',
              'SSE connection to playbook progress stream failed',
              'system'
            ));
          };
          
          eventSource.addEventListener('message', (event) => {
            let data: any;
            try {
              data = JSON.parse(event.data);
            } catch (parseError) {
              console.warn('[playbook_debug] Failed to parse SSE completion event:', parseError);
              return;
            }
            
            if (data.type === 'complete') {
              const result = data.result || {};
              const createdCount = result.createdEntityIds?.length || 0;
              
              console.log('[playbook_debug] ========================================');
              console.log('[playbook_debug] Playbook tool execution finished');
              console.log('[playbook_debug] Status:', data.status);
              console.log('[playbook_debug] Created entities:', result.createdEntityIds);
              console.log('[playbook_debug] Tool calls:', result.toolCallCount);
              console.log('[playbook_debug] Duration:', result.duration);
              console.log('[playbook_debug] ========================================');
              
              console.log(`[playbook_debug] Playbook completed - created ${createdCount} entities`);
              
              if (data.status === 'succeeded') {
                notificationService.emit({
                  type: 'automation_complete',
                  title: `${playbook.name} completed`,
                  message: `Playbook finished successfully`,
                  playbookId: playbook._id,
                  playbookRunId,
                  jobId,
                  status: 'succeeded',
                  result: result
                });
                finish(createSuccessResult(
                  `Playbook "${playbook.name}" completed`,
                  {
                    jobId,
                    playbookRunId,
                    status: data.status,
                    createdEntityIds: result.createdEntityIds || [],
                    toolCallCount: result.toolCallCount || 0,
                    duration: result.duration
                  }
                ));
              } else {
                notificationService.emit({
                  type: 'automation_error',
                  title: `${playbook.name} failed`,
                  message: data.error?.message || 'Playbook execution failed',
                  playbookId: playbook._id,
                  playbookRunId,
                  jobId,
                  status: 'failed',
                  result: data.error
                });
                finish(createErrorResult(
                  'Playbook execution failed',
                  data.error?.message || 'Unknown error',
                  'execution'
                ));
              }
            } else if (data.error) {
              console.error('[playbook_debug] SSE error event:', data.error);
              notificationService.emit({
                type: 'automation_error',
                title: `${playbook.name} failed`,
                message: data.error || 'Playbook execution failed',
                playbookId: playbook._id,
                playbookRunId,
                jobId,
                status: 'failed'
              });
              finish(createErrorResult('Playbook execution failed', data.error, 'system'));
            }
          });
          
          // Timeout after 30 minutes — finish() clears this automatically
          timeoutId = setTimeout(() => {
            console.error('[playbook_debug] Playbook execution timeout (30 minutes)');
            finish(createErrorResult(
              'Playbook timeout',
              'Playbook execution exceeded 30 minute limit',
              'timeout'
            ));
          }, 30 * 60 * 1000);
        });
        
      } catch (error: any) {
        console.error('[playbook_debug] ========================================');
        console.error('[playbook_debug] Playbook tool execution FAILED');
        console.error('[playbook_debug] Error:', error.message);
        console.error('[playbook_debug] Stack:', error.stack);
        console.error('[playbook_debug] ========================================');
        
        return createErrorResult(
          'Playbook execution failed',
          error.message,
          'execution'
        );
      }
    },
    
    formatViewData: (result) => ({
      type: 'playbook_execute',
      message: result.message,
      data: {
        playbookRunId: result.data?.playbookRunId,
        jobId: result.data?.jobId,
        createdEntityIds: result.data?.createdEntityIds || [],
        toolCallCount: result.data?.toolCallCount || 0,
        duration: result.data?.duration
      },
      structuredProgress: (result as any).view?.structuredProgress
    })
  } as SelfContainedCommand,
  
  // search_tools — exact-name tool lookup. Returns the full manifest definition as text.
  {
    id: 'search_tools',
    description: SEARCH_TOOLS_MANIFEST.description,
    autonomyLevel: 'auto',
    visualTreatment: 'silent',
    schema: {
      parameters: [
        {
          name: 'name',
          type: 'string',
          description: 'Exact tool name from the AVAILABLE_TOOLS list',
          required: true,
        },
      ],
    },
    handler: async (input: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
      const toolName: string = input?.name ?? '';
      return createSuccessResult('search_tools', {
        status: 'not_found',
        message: `No tool named "${toolName}". Check the AVAILABLE_TOOLS list in the system prompt for exact names.`,
      });
    },
  } as SelfContainedCommand,

  // Integration and context commands
  ...contextCommands,
  ...integrationCommands
];

console.log('[aiCommands] Exported commands:', {
  total: aiCommands.length,
  ids: aiCommands.map(c => c.id),
  hasPlaybookExecute: aiCommands.some(c => c.id === 'playbook_execute'),
  hasContextDiscovery: aiCommands.some(c => c.id === 'context_discover'),
  hasConnectIntegration: aiCommands.some(c => c.id === 'connect_integration')
}); 